import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';
import { api, ApiError, CallMessage, ChatCall, ChatCallParticipant, ChatEvent, ChatPerson } from '../../api';
import { useApp } from '../../App';
import { STREAM_OPENED, useChat } from '../ChatProvider';
import { playSound, startLoop, stopLoops } from '../../lib/sounds';
import { closeShown } from '../notifications';
import { useIsMobile } from '../../lib/media';
import { markActivity } from '../../lib/idle';
import { hasCallEntry, popCallEntry, pushCallEntry, watchCallPop } from './callHistory';
import { applySpeaker, Facing, InputKind, saveDevice } from './devices';
import { canShare as canShareMath, mixerGains, mixerNextChange } from './callMath';
import {
  CallCtx,
  Core,
  EMPTY,
  MediaFlags,
  applyJoined,
  buildParticipants,
  callBusy,
  camOptions,
  connectText,
  deviceDenied,
  deviceHint,
  deviceRefusal,
  disconnectText,
  failedJoin,
  foreignCallEvent,
  levelOf,
  micOptions,
  ownJoin,
  readRoom,
  ringingTooOld,
  isRingingCallTag,
  readStrip,
  sameTracks,
  saveStrip,
  screenSupported,
  shouldStopScreenShare,
  trackKeys as trackKeysOf,
  encodeSignal,
  decodeSignal,
  type Reaction,
  type RoomSignal,
  type CallKind,
  type CallLayout,
  type ShareSurface,
  type TrackMap,
  type TrackSlot,
} from './callRoom';

/*
 * Звонки между сотрудниками.
 *
 * Медиа больше не ходит напрямую между браузерами: звонок — это комната
 * LiveKit, и десять участников не рассылают девять копий видео каждый.
 * Соединением, дорожками и переключением устройств занимается библиотека,
 * здесь остаётся ход самого звонка — кто позван, кто вошёл, кто вышел, —
 * и он по-прежнему идёт через наш поток событий, а не через комнату:
 * приглашение нужно доставить тому, кто в комнату ещё не входил.
 */

export type {
  CallCtx,
  CallKind,
  CallLayout,
  ShareSurface,
  TrackSlot,
} from './callRoom';
export { screenSupported } from './callRoom';

const Ctx = createContext<CallCtx | null>(null);
export const useCall = () => useContext(Ctx)!;

/**
 * Гость: то, что страница комнаты уже получила от `roomJoin`. Провайдер сам
 * представиться не может — имя спрашивает дверь, — поэтому пропуск в комнату
 * медиасервера и вид звонка приходят готовыми.
 */
export type GuestEntry = {
  /** Код ссылки комнаты: по нему идут поток событий и выход. */
  token: string;
  participantId: string;
  /** Адрес медиасервера и пропуск в его комнату. */
  url: string;
  mediaToken: string;
  call: ChatCall;
};

/** Сколько живёт реакция на плитке: семь секунд (решение Ильи 11.09.2026).
 *  Рука в это число не входит — она висит, пока её не опустят, и лишь
 *  перекрывается реакцией на эти семь секунд. */
const REACTION_MS = 7000;

/**
 * Как часто вкладка напоминает про идущий звонок: сильно чаще, чем 15 минут
 * автовыхода, и достаточно редко, чтобы не быть шумом.
 */
const ACTIVITY_PING_MS = 60_000;

/**
 * Через сколько вкладка сама проверяет, жив ли показанный входящий. Чуть
 * больше минуты дозвона на сервере (RING_TIMEOUT_MS) — с запасом на дорогу.
 */
const RING_WATCH_MS = 75_000;

export function CallProvider({
  children,
  guest,
}: {
  children: React.ReactNode;
  guest?: GuestEntry;
}) {
  // У гостя ни учётной записи, ни чата: оба провайдера над ним не стоят и
  // отдают null. Всё, что из них берётся, читается только в режиме сотрудника.
  const app = useApp();
  const chat = useChat();
  const isGuest = !!guest;
  // Метка этой вкладки. Учётная запись живёт на нескольких устройствах, и по
  // событию «вошёл» без метки не отличить свой вход от чужого: раньше это
  // угадывалось по наличию комнаты, и вкладка звонящего иногда закрывала
  // экран сама себе, не сказав об этом серверу (случай Ильи 09.09.2026).
  const tabId = useRef(Math.random().toString(36).slice(2));
  const me = guest ? `guest:${guest.participantId}` : app.user.id;
  const mobile = useIsMobile();

  const [core, setCore] = useState<Core>(EMPTY);
  /** Список звонка с сервера: имена, фото и статусы приглашений. */
  const [roster, setRoster] = useState<ChatCallParticipant[]>([]);
  /** Микрофон, камера и экран каждого — из публикаций комнаты. */
  const [media, setMedia] = useState<Record<string, MediaFlags>>({});
  const [speakers, setSpeakers] = useState<string[]>([]);
  const speakersRef = useRef<string[]>([]);
  /** Когда каждый говорил в последний раз — для удержания автомикшера. */
  const lastHeard = useRef<Record<string, number>>({});
  /** Пересчёт громкостей по концу удержания: кто-то замолчал достаточно давно. */
  const [mixTick, setMixTick] = useState(0);
  const [hands, setHands] = useState<Record<string, boolean>>({});
  /** Реакция в звонке одна на всех и одна в каждый момент (решение Ильи
   *  11.09.2026): новая вытесняет прежнюю, живёт семь секунд. */
  const [reaction, setReaction] = useState<{ from: string; emo: Reaction } | null>(null);
  /** Переписка звонка: лента и счётчик непрочитанного, пока панель закрыта. */
  const [messages, setMessages] = useState<CallMessage[]>([]);
  const [unread, setUnread] = useState(0);
  /** Метки дорожек по слотам: по своей плитка понимает, что пора перецепиться. */
  const [trackKeys, setTrackKeys] = useState<Record<string, string>>({});

  const coreRef = useRef(core);
  coreRef.current = core;
  const rosterRef = useRef(roster);
  rosterRef.current = roster;
  const guestRef = useRef(guest);
  guestRef.current = guest;
  const handsRef = useRef(hands);
  /** Таймер гашения реакции: новая сбрасывает прежний. */
  const reactionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const meRef = useRef(me);
  meRef.current = me;
  /* Отправка сигнала и показ реакции объявлены ниже по файлу: кнопка реакции
     зовёт их через ссылки, чтобы не тянуть объявления наверх. */
  const signalRef = useRef<((sig: RoomSignal) => void) | null>(null);
  const reactionRef = useRef<((from: string, emo: Reaction) => void) | null>(null);

  const room = useRef<Room | null>(null);
  /** Подписчики живого слоя доски (раздел 4.2 спеки) — переживают переподключение комнаты. */
  const boardDataHandlers = useRef<Set<(bytes: Uint8Array, from: string) => void>>(new Set());
  const callId = useRef<string | null>(null);
  /** Дорожки по участнику: камера, экран и звук приходят по отдельности. */
  const tracks = useRef<TrackMap>(new Map());
  /** Сторона камеры на телефоне: фронтальная ↔ задняя. */
  const facing = useRef<Facing>('user');
  /** Уходим сами — разрыв комнаты не считается потерей связи. */
  const leaving = useRef(false);
  /**
   * Вход в комнату идёт прямо сейчас. Своё же событие `joined` сервер
   * рассылает раньше, чем отвечает на запрос входа: без этого признака оно
   * читалось бы как ответ на другом устройстве и сбрасывало бы звонок.
   * Он же не даёт начать второй звонок, пока не закончился первый вход.
   */
  const joining = useRef(false);
  /**
   * Идёт подключение к комнате. Библиотека при неудачном входе сперва шлёт
   * `Disconnected`, и только потом отклоняет обещание: без этого признака
   * обработчик разрыва успевал показать «Связь потеряна» и закрыть звонок, а
   * настоящая причина отказа терялась — `abandoned()` глотал её, потому что
   * комнату уже обнулил finish().
   */
  const connecting = useRef(false);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Таймер показа экрана «ended» (4 с): без отмены старый таймер стирает новый звонок. */
  const endedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearEndedTimer = () => {
    if (endedTimer.current) clearTimeout(endedTimer.current);
    endedTimer.current = null;
  };

  /** Короткое уведомление в шапке — само гаснет через 3 с. */
  /**
   * Короткая надпись в шапке звонка. `hold` — оставить её до конца звонка:
   * «человека нет в сети» это не мгновенная новость, а причина, по которой
   * никто не берёт трубку всю минуту дозвона (правка 21.09.2026).
   */
  const notify = useCallback((text: string, hold = false) => {
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = null;
    setCore((s) => ({ ...s, notice: text }));
    if (hold) return;
    noticeTimer.current = setTimeout(() => setCore((s) => ({ ...s, notice: '' })), 3000);
  }, []);

  /** Перечитать комнату целиком — почему целиком, сказано у readRoom. */
  const syncRoom = useCallback(() => {
    const r = room.current;
    if (!r) return;
    const snap = readRoom(r);
    const changed = !sameTracks(tracks.current, snap.tracks);
    tracks.current = snap.tracks;
    setMedia(snap.flags);
    // метки пересчитываем только на смене состава дорожек: иначе каждый mute
    // и каждое событие комнаты заставляли бы плитки перецеплять видео
    if (changed) setTrackKeys(trackKeysOf(snap.tracks));
    // мои кнопки показывают то же, что публикации: системная «Остановить
    // показ» и отказ в доступе к камере иначе разошлись бы с панелью
    setCore((s) =>
      s.micOn === snap.mine.micOn && s.camOn === snap.mine.camOn && s.screenOn === snap.mine.screenOn
        ? s
        : { ...s, ...snap.mine },
    );
  }, []);

  // сама функция неизменна — она читает ref; о смене дорожек плитки узнают
  // из trackKeys, а не из новой ссылки на attach
  const attach = useCallback((el: HTMLMediaElement, userId: string, source: TrackSlot) => {
    const track = tracks.current.get(userId)?.[source];
    if (!track) return () => {};
    track.attach(el);
    return () => {
      track.detach(el);
    };
  }, []);

  /** Сказать серверу, что я вышел: у гостя маршруты звонка закрыты. */
  const leaveCall = useCallback((successorId?: string) => {
    const g = guestRef.current;
    if (g) void api.roomLeave(g.token).catch(() => {});
    else if (callId.current) void api.callLeave(callId.current, successorId).catch(() => {});
  }, []);

  const cleanup = useCallback(() => {
    clearEndedTimer();
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = null;
    leaving.current = true;
    void room.current?.disconnect().catch(() => {});
    room.current = null;
    tracks.current = new Map();
    callId.current = null;
    facing.current = 'user';
    handsRef.current = {};
    setMedia({});
    setTrackKeys({});
    setSpeakers([]);
    speakersRef.current = [];
    lastHeard.current = {};
    setHands({});
  }, []);

  const finish = useCallback(
    (error = '') => {
      cleanup();
      // вид и выбор динамика переживают звонок: это настройки, а не разговор
      setCore((s) => ({
        ...EMPTY,
        phase: error ? 'ended' : 'idle',
        error,
        view: s.view,
        speakerId: s.speakerId,
        stripShown: s.stripShown,
      }));
      if (error) {
        // список нужен ещё четыре секунды: экран «Звонок отклонён» подписан именем
        endedTimer.current = setTimeout(() => {
          setCore(EMPTY);
          setRoster([]);
        }, 4000);
      } else setRoster([]);
    },
    [cleanup],
  );

  /** Принять вид звонка с сервера: список, администратор, ведущий. */
  const applyView = useCallback((view: ChatCall) => {
    callId.current = view.id;
    setRoster(view.participants);
    setCore((s) => ({
      ...s,
      callId: view.id,
      kind: view.kind,
      adminId: view.adminId,
      presenterId: view.presenterId,
      boardHostId: view.boardHostId,
      screenShareHolderId: view.screenShareHolderId,
      channelId: view.channelId,
      linkToken: view.linkToken,
      meeting: view.meeting,
      timerEndsAt: view.timerEndsAt,
    }));
  }, []);

  /**
   * Вход сорвался. Одно место на все ветки: сервер уже знает, что человек
   * входит, и обязан узнать, что он не вошёл (см. failedJoin). Штатный выход
   * идёт мимо: там об уходе сообщает hangup, а обработчик разрыва молчит,
   * пока поднят leaving, — второго сообщения не будет.
   */
  const failJoin = useCallback(
    (e: unknown) => finish(failedJoin({ callId: callId.current, error: e, leave: api.callLeave })),
    [finish],
  );

  /**
   * Перечитать список: администратор мог позвать человека, о котором эта
   * вкладка ничего не знает — приглашение уходит только самому позванному.
   */
  const syncRoster = useCallback(() => {
    const id = callId.current;
    // Гостю перечитывать нечем: маршрут текущего звонка требует учётной
    // записи. Список у него тот, что пришёл при входе, плюс правки событий.
    if (!id || guestRef.current) return;
    api
      .callCurrent()
      .then((cur) => {
        if (cur && cur.id === callId.current) applyView(cur);
      })
      .catch(() => {});
  }, [applyView]);

  const setStatus = useCallback(
    (userId: string, status: ChatCallParticipant['status']) => {
      if (!rosterRef.current.some((p) => p.id === userId)) {
        syncRoster();
        return;
      }
      setRoster((list) => list.map((p) => (p.id === userId ? { ...p, status } : p)));
    },
    [syncRoster],
  );

  /** Подключиться к комнате и опубликовать себя. */
  const connectRoom = useCallback(
    async (url: string, token: string, kind: CallKind) => {
      const r = new Room({
        adaptiveStream: true,
        dynacast: true,
        audioCaptureDefaults: micOptions(),
        videoCaptureDefaults: camOptions(facing.current),
      });
      room.current = r;
      leaving.current = false;
      r.on(RoomEvent.ParticipantConnected, () => {
        syncRoom();
        // вошёл кто-то, кого эта вкладка не знает: приглашение уходит только
        // самому позванному, список приходится перечитывать
        syncRoster();
      })
        .on(RoomEvent.ParticipantDisconnected, syncRoom)
        .on(RoomEvent.TrackSubscribed, (track) => {
          syncRoom();
          // первый чужой звук: до подписки разрешать было нечего, а браузер
          // мог отказать в автозапуске уже после ответа на звонок
          if (track.kind === Track.Kind.Audio) void r.startAudio().catch(() => {});
        })
        .on(RoomEvent.TrackUnsubscribed, syncRoom)
        // Показ экрана нельзя приглушить, его снимают с публикации целиком.
        // «Отписали» библиотека шлёт раньше, чем удаляет публикацию, поэтому
        // пересчёт по нему ещё видит показ живым: у забравшего право оставался
        // чёрный кадр, и начать свой показ он не мог, пока кто-нибудь не
        // щёлкнет микрофоном. Пересчитываем и по снятию публикации.
        .on(RoomEvent.TrackUnpublished, syncRoom)
        .on(RoomEvent.AudioPlaybackStatusChanged, () => {
          setCore((s) => ({ ...s, audioBlocked: !r.canPlaybackAudio }));
        })
        .on(RoomEvent.TrackMuted, syncRoom)
        .on(RoomEvent.TrackUnmuted, syncRoom)
        .on(RoomEvent.LocalTrackPublished, syncRoom)
        // системная кнопка браузера «Остановить показ» приходит сюда же:
        // публикации не стало — syncRoom погасит screenOn
        .on(RoomEvent.LocalTrackUnpublished, syncRoom)
        .on(RoomEvent.ActiveSpeakersChanged, (list) => {
          // Автомикшеру нужен миг, когда человек замолчал: медиасервер шлёт
          // список только при его смене, поэтому отмечаем и оставшихся, и
          // выбывших — у выбывших это и есть конец речи.
          const ids = list.map((p) => p.identity);
          const at = Date.now();
          for (const id of new Set([...speakersRef.current, ...ids])) lastHeard.current[id] = at;
          speakersRef.current = ids;
          setSpeakers(ids);
        })
        // атрибут `screen` — умеет ли браузер участника показывать экран
        .on(RoomEvent.ParticipantAttributesChanged, syncRoom)
        // Живой слой доски (раздел 4.2 спеки): канал данных комнаты, уже
        // поднятый для звонка, разносит законченные штрихи мгновенно, минуя
        // сервер приложения. Сама доска (Board.tsx) не знает про LiveKit —
        // подписывается через onBoardData/sendBoardData ниже.
        .on(RoomEvent.DataReceived, (payload, participant) => {
          const from = participant?.identity ?? '';
          // Рука и реакции идут этим же каналом: разбираем их первыми, а всё
          // остальное отдаём доске. Автор — личность отправителя, проверенная
          // медиасервером, а не поле внутри тела.
          const sig = decodeSignal(payload);
          if (sig) {
            if (sig.s === 'hand') {
              if (!handsRef.current[from] && sig.on) playSound('hand');
              handsRef.current = { ...handsRef.current, [from]: sig.on };
              setHands(handsRef.current);
            } else {
              showReaction(from, sig.emo);
            }
            return;
          }
          boardDataHandlers.current.forEach((h) => h(payload, from));
        })
        .on(RoomEvent.ConnectionQualityChanged, (q, p) => {
          if (p.identity !== me) return;
          setCore((s) => ({ ...s, quality: levelOf(q) }));
        })
        .on(RoomEvent.Reconnecting, () => setCore((s) => ({ ...s, reconnecting: true })))
        .on(RoomEvent.Reconnected, () => {
          setCore((s) => ({ ...s, reconnecting: false }));
          // За время обрыва состав дорожек мог поменяться, а события об этом
          // прошли мимо: пересчитываем, иначе показ или камера залипнут до
          // первого постороннего события.
          syncRoom();
        })
        .on(RoomEvent.Disconnected, (reason) => {
          // ушли сами или это отголосок прошлого звонка — не наша беда
          if (leaving.current || room.current !== r) return;
          // вход ещё идёт: отказ во входе расскажет сам connect(), и расскажет
          // подробнее — здесь его перебивать нечем
          if (connecting.current) return;
          // сдались сами — сервер об этом не знает, карточку без него не получить
          leaveCall();
          finish(disconnectText(reason));
        });

      // звонок могли сбросить, пока шло подключение: тогда эта комната уже
      // ничья — её нужно тихо закрыть, а не показывать её ошибки человеку
      const abandoned = async () => {
        if (room.current === r) return false;
        await r.disconnect().catch(() => {});
        return true;
      };

      try {
        connecting.current = true;
        try {
          await r.connect(url, token);
        } finally {
          connecting.current = false;
        }
        if (await abandoned()) return;
        // Ведущий не предлагает право показа тому, чей браузер экран не
        // показывает (спека 2026-09-25-guest-screen-share, раздел 5). Не
        // принял медиасервер — ничего: молчание читается как «умеет».
        void r.localParticipant.setAttributes({ screen: screenSupported ? '1' : '0' }).catch(() => {});
        // Safari разрешает звук только из жеста: «Позвонить» и «Ответить» — он и есть
        void r.startAudio().catch(() => {});
        setCore((s) => ({ ...s, audioBlocked: !r.canPlaybackAudio }));
        // Браузер может не дать микрофон или камеру: слушать разговор это не
        // мешает, и устройство разрешают уже по ходу. Поэтому отказ не рвёт
        // вход, а объясняется надписью в шапке звонка.
        try {
          await r.localParticipant.setMicrophoneEnabled(true, micOptions());
        } catch (e) {
          if (!deviceRefusal(e)) throw e;
          setCore((s) => ({ ...s, micOn: false }));
          notify(deviceHint(e, 'микрофон'));
        }
        if (await abandoned()) return;
        if (kind === 'video') {
          try {
            await r.localParticipant.setCameraEnabled(true, camOptions(facing.current));
          } catch (e) {
            if (!deviceRefusal(e)) throw e;
            // Надпись в шапке не ставим: она висит поверх счётчика времени
            // (замечание Ильи 15.09.2026). Состояние видно на самой кнопке
            // камеры, а словами объясняем коротким уведомлением.
            setCore((s) => ({ ...s, camOn: false, camBlocked: deviceDenied(e) }));
            notify(deviceHint(e, 'камера'));
          }
        }
        if (await abandoned()) return;
        syncRoom();
      } catch (e) {
        // сбой брошенной комнаты не должен всплыть ошибкой поверх нового звонка
        if (await abandoned()) return;
        throw e;
      }
    },
    [finish, leaveCall, me, syncRoom, syncRoster],
  );

  /** Общее начало исходящего звонка: обнулить состояние, кроме настроек. */
  const startOutgoing = useCallback((kind: CallKind) => {
    clearEndedTimer();
    setRoster([]);
    setCore((s) => ({
      ...EMPTY,
      phase: 'outgoing',
      kind,
      camOn: kind === 'video',
      view: s.view,
      speakerId: s.speakerId,
      stripShown: s.stripShown,
    }));
  }, []);

  /** Создать звонок и сразу войти в комнату: администратор ждёт в ней остальных. */
  const enter = useCallback(
    async (started: ChatCall, kind: CallKind) => {
      joining.current = true;
      try {
        applyView(started);
        const joined = await api.callJoin(started.id, tabId.current);
        applyView(joined.call);
        await connectRoom(joined.url, joined.token, kind);
      } finally {
        joining.current = false;
      }
    },
    [applyView, connectRoom],
  );

  const call = useCallback(
    async (person: ChatPerson, kind: CallKind) => {
      // уже в комнате или на полпути в неё — второй звонок затёр бы первый
      if (joining.current || room.current) return;
      joining.current = true;
      try {
        startOutgoing(kind);
        // имя нужно шапке ещё до ответа сервера
        setRoster([{ ...person, kind: 'user', status: 'ringing' }]);
        const started = await api.callStart({ userId: person.id, kind });
        // Не в сети — не повод закрывать звонок: у разговора есть ссылка, и
        // человека зовут ею (решение Ильи 09.09.2026). Раньше экран закрывался.
        if ('offline' in started && started.offline) {
          notify(`${person.name} сейчас не в сети — позовите ссылкой из окна приглашения`, true);
        }
        await enter(started, kind);
      } catch (e) {
        failJoin(e);
      } finally {
        joining.current = false;
      }
    },
    [enter, failJoin, notify, startOutgoing],
  );

  const callChannel = useCallback(
    async (channelId: string, kind: CallKind) => {
      if (joining.current || room.current) return;
      joining.current = true;
      try {
        startOutgoing(kind);
        await enter(await api.callStart({ channelId, kind }), kind);
      } catch (e) {
        // в канале больше десяти человек — сервер просит выбрать, кого звать
        if (e instanceof ApiError && e.status === 400 && e.message.includes('выберите')) {
          finish();
          notify(e.message);
          setCore((s) => ({ ...s, needPick: true }));
          return;
        }
        failJoin(e);
      } finally {
        joining.current = false;
      }
    },
    [enter, failJoin, finish, notify, startOutgoing],
  );

  /**
   * Звонок выбранным людям: в канале не поместилось десяти мест, и состав
   * задал человек. Дальше всё как у звонка команде — сервер сам решает, кто
   * администратор, и рассылает приглашения.
   */
  const callPeople = useCallback(
    async (userIds: string[], kind: CallKind) => {
      if (joining.current || room.current || !userIds.length) return;
      joining.current = true;
      try {
        startOutgoing(kind);
        await enter(await api.callStart({ userIds, kind }), kind);
      } catch (e) {
        failJoin(e);
      } finally {
        joining.current = false;
      }
    },
    [enter, failJoin, startOutgoing],
  );

  const invite = useCallback(
    async (userIds: string[]) => {
      const id = callId.current;
      if (!id) return;
      try {
        applyView(await api.callInvite(id, userIds));
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Не удалось позвать');
      }
    },
    [applyView, notify],
  );

  const accept = useCallback(async () => {
    const id = callId.current;
    // двойное нажатие «Ответить» подключило бы вторую комнату поверх первой
    if (!id || joining.current || room.current) return;
    joining.current = true;
    const kind = coreRef.current.kind;
    try {
      const joined = await api.callJoin(id, tabId.current);
      applyView(joined.call);
      setCore((s) => ({ ...s, phase: 'active', camOn: kind === 'video' }));
      await connectRoom(joined.url, joined.token, kind);
    } catch (e) {
      failJoin(e);
    } finally {
      joining.current = false;
    }
  }, [applyView, connectRoom, failJoin]);

  /**
   * Вход в постоянную комнату звонка встречи календаря.
   *
   * Это обычный звонок: сервер заводит ту же карточку, что и у звонка из чата,
   * только комната у неё постоянная и своя (calls.service.joinMeeting).
   * Отдельного механизма у комнаты встречи больше нет — без карточки у вкладки
   * не было идентификатора звонка, а по нему слой отличает события своего
   * разговора от чужих, сообщает серверу об уходе и берёт список участников.
   */
  const joinMeeting = useCallback(
    async (eventId: string) => {
      // уже в комнате или на полпути в неё — вторая затёрла бы первую
      if (joining.current || room.current) return;
      joining.current = true;
      // Экран звонка открываем только после ответа сервера: комната встречи
      // может быть закрыта до времени, и мигать разговором ради отказа незачем
      // (решение Ильи 09.09.2026). Отказ уходит наружу — его показывает
      // карточка встречи всплывающим окном.
      let opened = false;
      try {
        const joined = await api.calRoomJoin(eventId);
        startOutgoing('audio');
        opened = true;
        applyView(joined.call);
        setCore((s) => ({ ...s, phase: 'active' }));
        await connectRoom(joined.url, joined.token, 'audio');
      } catch (e) {
        if (opened) failJoin(e);
        else throw e;
      } finally {
        joining.current = false;
      }
    },
    [applyView, connectRoom, failJoin, startOutgoing],
  );

  const decline = useCallback(() => {
    const id = callId.current;
    if (id) void api.callDecline(id).catch(() => {});
    finish();
  }, [finish]);

  /** `successorId` — выбранный уходящим ведущим преемник (спека 3.1). */
  const hangup = useCallback(
    (successorId?: string) => {
      leaveCall(successorId);
      finish();
    },
    [finish, leaveCall],
  );

  const toggleMic = useCallback(() => {
    const on = !coreRef.current.micOn;
    setCore((s) => ({ ...s, micOn: on }));
    void room.current?.localParticipant
      .setMicrophoneEnabled(on, micOptions())
      // не дали устройство — кнопка возвращается в прежнее положение, иначе
      // человек считал бы, что его слышно
      .catch((e) => setCore((s) => ({ ...s, micOn: false, error: connectText(e) })));
  }, []);

  /** Камеру можно включить посреди разговора — звонок начинался как голосовой. */
  const toggleCam = useCallback(() => {
    const on = !coreRef.current.camOn;
    setCore((s) => ({ ...s, camOn: on }));
    void room.current?.localParticipant
      .setCameraEnabled(on, camOptions(facing.current))
      // Попытка повторяется при каждом нажатии: человек мог разрешить камеру
      // в настройках сайта, и тогда она включится без перезахода в звонок.
      .then(() => setCore((s) => ({ ...s, camBlocked: false })))
      .catch((e) => {
        setCore((s) => ({ ...s, camOn: false, camBlocked: deviceDenied(e) }));
        notify(deviceHint(e, 'камера'));
      });
  }, [notify]);

  const toggleHand = useCallback(() => {
    const hand = !coreRef.current.hand;
    setCore((s) => ({ ...s, hand }));
    // Рука — не медиа, комната о ней ничего не знает. Раньше она уходила
    // запросом к серверу, а его гостю не сделать: учётной записи нет. Теперь
    // идёт каналом данных комнаты, как живой слой доски, — и гость поднимает
    // руку наравне со всеми (решение Ильи 11.09.2026).
    sendSignal({ s: 'hand', on: hand });
  }, []);

  /** Реакция: живёт три секунды и гаснет сама; рука висит, пока её не опустят. */
  const sendReaction = useCallback((emo: Reaction) => {
    // рука опускается вместе с реакцией — и у себя, и у всех остальных
    if (coreRef.current.hand) signalRef.current?.({ s: 'hand', on: false });
    signalRef.current?.({ s: 'reaction', emo });
    // свою видно сразу, не дожидаясь эха канала
    reactionRef.current?.(meRef.current, emo);
  }, []);

  const stopScreen = useCallback(() => {
    setCore((s) => ({ ...s, screenOn: false, shareSurface: null }));
    void room.current?.localParticipant.setScreenShareEnabled(false).catch(() => {});
  }, []);

  const startScreen = useCallback(
    async (surface?: ShareSurface) => {
      const r = room.current;
      if (!r) return;
      try {
        await r.localParticipant.setScreenShareEnabled(true, {
          audio: false,
          // чертежи и таблицы: важна чёткость, а не плавность
          contentHint: 'text',
          // открыть системный выбор сразу на окнах или экранах, не предлагать
          // нашу же вкладку и дать сменить окно по ходу показа
          video: surface ? { displaySurface: surface } : true,
          selfBrowserSurface: 'exclude',
          surfaceSwitching: 'include',
        });
        // Что именно показывают, решает человек в системном окне, а не наша
        // подсказка: берём фактическое значение у дорожки — от него зависит,
        // можно ли рисовать поверх карточку сообщения (спека 8).
        const pub = r.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        const actual = pub?.track?.mediaStreamTrack.getSettings().displaySurface ?? surface ?? null;
        setCore((s) => ({ ...s, screenOn: true, layout: 'speaker', shareSurface: actual }));
      } catch (e) {
        // отмена системного диалога — не ошибка
        if ((e as DOMException).name !== 'NotAllowedError') notify('Не удалось показать экран');
      }
    },
    [notify],
  );

  /**
   * Передать роль ведущего: доска, участники и отсчёт уходят с ней целиком.
   * Право показа экрана с 25.09.2026 снова отдельное (`setScreenShare`), а
   * выданное прежним ведущим сервер при передаче роли снимает.
   */
  const setHost = useCallback(
    async (userId: string) => {
      const id = callId.current;
      if (!id) return;
      try {
        await api.callHost(id, userId);
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Не удалось передать роль ведущего');
      }
    },
    [notify],
  );

  /** Выдать право показа экрана или вернуть себе (спека 2026-09-25-guest-screen-share). */
  const setScreenShare = useCallback(
    async (participantId: string | null) => {
      const id = callId.current;
      if (!id) return;
      try {
        await api.callScreenShare(id, participantId);
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Не удалось передать право показа');
      }
    },
    [notify],
  );

  const kick = useCallback(
    async (participantId: string) => {
      const id = callId.current;
      if (!id) return;
      try {
        await api.callKick(id, participantId);
      } catch (e) {
        notify(e instanceof Error ? e.message : 'Не удалось удалить участника');
      }
    },
    [notify],
  );

  const closeRoom = useCallback(async () => {
    const id = callId.current;
    if (!id) return;
    try {
      await api.callClose(id);
    } catch (e) {
      notify(e instanceof Error ? e.message : 'Не удалось завершить звонок');
    }
  }, [notify]);

  const switchInput = useCallback(
    async (kind: InputKind, deviceId: string) => {
      const r = room.current;
      if (!r) return;
      try {
        // exact = false: если устройство пропало, браузер вернётся к умолчанию,
        // а не оставит звонок без микрофона
        await r.switchActiveDevice(kind, deviceId, false);
        // запоминаем только удавшийся выбор — иначе следующий звонок начнётся
        // с устройства, которое уже сегодня не заработало
        saveDevice(kind, deviceId);
      } catch {
        notify('Устройство недоступно');
      }
    },
    [notify],
  );

  const switchSpeaker = useCallback((deviceId: string) => {
    setCore((s) => ({ ...s, speakerId: deviceId }));
    // элементы плиток библиотека не знает — динамик им ставим сами
    applySpeaker(deviceId);
    void room.current?.switchActiveDevice('audiooutput', deviceId).catch(() => {});
  }, []);

  /** Телефон: фронтальная ↔ задняя. Заднюю не зеркалим — текст на стройке читается как есть. */
  const flipCamera = useCallback(async () => {
    const r = room.current;
    if (!r || !coreRef.current.camOn) return;
    const next: Facing = facing.current === 'user' ? 'environment' : 'user';
    const opts = camOptions(next, true);
    try {
      // выбор камеры на телефоне задаётся стороной, а не идентификатором.
      // Съёмку пересобирает именно restartTrack: setCameraEnabled(false/true)
      // только снимает и возвращает mute у той же дорожки, а опции при этом
      // пропадают — камера оставалась бы прежней. Заодно restartTrack идёт
      // мимо videoCaptureDefaults, и сторона побеждает запомненный deviceId.
      const track = r.localParticipant.getTrackPublication(Track.Source.Camera)?.videoTrack;
      if (track) await track.restartTrack(opts);
      else await r.localParticipant.setCameraEnabled(true, opts);
      facing.current = next;
      setCore((s) => ({ ...s, mirror: next === 'user' }));
    } catch {
      setCore((s) => ({ ...s, error: 'Камера недоступна' }));
    }
  }, []);

  /** Звук после запрета автозапуска: возвращать его можно только из жеста. */
  const resumeAudio = useCallback(async () => {
    const r = room.current;
    if (!r) return;
    try {
      await r.startAudio();
      setCore((s) => ({ ...s, audioBlocked: !r.canPlaybackAudio }));
    } catch {
      /* не разрешили — признак остаётся, кнопка на месте */
    }
  }, []);

  /**
   * Живой слой доски (раздел 4.2 спеки): отправка всем в комнате, включая
   * гостей — LiveKit сам разносит данные всем присутствующим, без адресации.
   * Комнаты ещё нет или сеть подвела — тихо молчим: хранимый слой (обычный
   * запрос к серверу) всё равно донесёт штрих, живой канал — только ускоряет.
   */
  /** Сигнал звонка (рука, реакция) — тем же каналом данных, что и доска. */
  const sendSignal = useCallback((sig: RoomSignal) => {
    void room.current?.localParticipant
      .publishData(encodeSignal(sig) as Uint8Array<ArrayBuffer>, { reliable: true })
      .catch(() => {});
  }, []);

  /**
   * Показать реакцию и погасить её через семь секунд. Реакция в звонке одна:
   * новая вытесняет прежнюю. Поднятая рука при этом считается опущенной —
   * место одно, и висящая рука после отклика вводила бы в заблуждение
   * (решение Ильи 11.09.2026).
   */
  const showReaction = useCallback((from: string, emo: Reaction) => {
    if (!from) return;
    if (reactionTimer.current) clearTimeout(reactionTimer.current);
    setReaction({ from, emo });
    if (handsRef.current[from]) {
      handsRef.current = { ...handsRef.current, [from]: false };
      setHands(handsRef.current);
    }
    if (from === meRef.current && coreRef.current.hand) setCore((s) => ({ ...s, hand: false }));
    reactionTimer.current = setTimeout(() => {
      reactionTimer.current = null;
      setReaction(null);
    }, REACTION_MS);
  }, []);

  signalRef.current = sendSignal;
  reactionRef.current = showReaction;

  const sendBoardData = useCallback((bytes: Uint8Array) => {
    // TextEncoder().encode() в Board.tsx всегда отдаёт непубличный ArrayBuffer,
    // но общий тип Uint8Array этого не гарантирует — приведение только для типов.
    void room.current?.localParticipant
      .publishData(bytes as Uint8Array<ArrayBuffer>, { reliable: true })
      .catch(() => {});
  }, []);

  /** Подписка на входящие данные канала; переживает переподключение — обработчики хранятся отдельно от Room. */
  const onBoardData = useCallback((handler: (bytes: Uint8Array, from: string) => void) => {
    boardDataHandlers.current.add(handler);
    return () => {
      boardDataHandlers.current.delete(handler);
    };
  }, []);

  /** Прочитать ленту переписки целиком: открываем панель — забираем историю. */
  const loadMessages = useCallback(async () => {
    const g = guestRef.current;
    const id = callId.current;
    const list = g ? await api.roomMessages(g.token) : id ? await api.callMessages(id) : [];
    setMessages(list);
    setUnread(0);
  }, []);

  /** Написать в переписку звонка; своё сообщение придёт эхом события. */
  const sendMessage = useCallback(async (text: string, fileIds: number[] = []) => {
    const g = guestRef.current;
    const id = callId.current;
    const msg = g
      ? await api.roomMessagePost(g.token, text, fileIds)
      : id
        ? await api.callMessagePost(id, text, fileIds)
        : null;
    if (msg) setMessages((list) => (list.some((m) => m.id === msg.id) ? list : [...list, msg]));
  }, []);

  /** Загрузить вложение: номер файла уходит потом в сообщение. */
  const uploadMessageFile = useCallback(async (file: File) => {
    const g = guestRef.current;
    const id = callId.current;
    if (g) return api.roomMessageUpload(g.token, file);
    if (id) return api.callMessageUpload(id, file);
    throw new Error('Звонок не начат');
  }, []);

  const markChatRead = useCallback(() => setUnread(0), []);

  const minimize = useCallback(() => setCore((s) => ({ ...s, view: 'mini' })), []);
  const expand = useCallback(() => setCore((s) => ({ ...s, view: 'full' })), []);
  const setLayout = useCallback((layout: CallLayout) => setCore((s) => ({ ...s, layout })), []);
  const clearNeedPick = useCallback(() => setCore((s) => ({ ...s, needPick: false })), []);

  const toggleStrip = useCallback(() => {
    setCore((s) => {
      const stripShown = !s.stripShown;
      saveStrip(stripShown);
      return { ...s, stripShown };
    });
  }, []);

  // умолчание полосы разное на вебе и на телефоне; свой выбор лежит в памяти
  useEffect(() => {
    setCore((s) => ({ ...s, stripShown: readStrip(mobile) }));
  }, [mobile]);

  /** Разбор события звонка: у сотрудника оно приходит шиной чата, у гостя — своим потоком. */
  const onCallEvent = useCallback(
    (e: ChatEvent) => {
      if (e.type !== 'call') return;
      const data = e.data as {
        type: string;
        call?: ChatCall;
        callId?: string;
        userId?: string;
        /** Карточка участника: гостю неоткуда её взять самому, событие несёт готовую. */
        who?: ChatCallParticipant;
        tabId?: string;
        from?: string;
        byUserId?: string | null;
        payload?: { state?: { hand?: boolean } };
        reason?: string;
      };

      if (data.type === 'invite' && data.call) {
        /*
         * Второй звонок во время разговора отклоняем сразу. Но «в разговоре»
         * — это живой экран звонка, а не просто запомненный номер (баг Ильи
         * 21.09.2026): номер мог остаться от разговора, который кончился
         * незаметно для вкладки, и тогда она молча отклоняла все входящие —
         * люди переставали видеть звонки друг друга, не понимая почему.
         * Экрана нет — номер устарел, забываем его и звонок принимаем.
         */
        if (callBusy(coreRef.current.phase, callId.current)) {
          void api.callDecline(data.call.id).catch(() => {});
          return;
        }
        callId.current = null;
        clearEndedTimer();
        const view = data.call;
        setCore((s) => ({
          ...EMPTY,
          phase: 'incoming',
          kind: view.kind,
          view: s.view,
          speakerId: s.speakerId,
          stripShown: s.stripShown,
          camOn: view.kind === 'video',
        }));
        applyView(view);
        return;
      }

      // события чужого звонка (например, «завершён» для прошлого, только что
      // закрытого) сюда не относятся — в том числе когда своего звонка ещё нет
      if (foreignCallEvent(data.type, data.callId, callId.current)) return;

      if (data.type === 'joined' && data.userId) {
        if (data.userId === me) {
          const what = ownJoin(coreRef.current.phase, data.tabId, tabId.current, !!room.current || joining.current);
          if (what === 'mine') setStatus(me, 'active');
          // трубку взяли в соседней вкладке или на другом устройстве: экран
          // входящего просто гаснет, без надписи (решение Ильи 02.10.2026)
          else if (what === 'close') finish('');
          else if (what === 'elsewhere') finish('Ответили на другом устройстве');
          return;
        }
        // вошедшего после меня в составе ещё нет — событие несёт его карточку
        if (data.who) setRoster((list) => applyJoined(list, data.who!));
        setStatus(data.userId, 'active');
        // дозвонились: собеседник в комнате
        setCore((s) => (s.phase === 'outgoing' ? { ...s, phase: 'active' } : s));
        return;
      }

      if (data.type === 'left' && data.userId) {
        setStatus(data.userId, 'left');
        return;
      }

      if (data.type === 'declined' && data.userId) {
        // отклонил я сам в другой вкладке или на другом устройстве, или
        // истёк мой дозвон — экран входящего гаснет и здесь, в групповом
        // звонке тоже (раньше остальные звенели до минуты)
        if (data.userId === me && coreRef.current.phase === 'incoming') {
          finish('');
          return;
        }
        setStatus(data.userId, 'declined');
        return;
      }

      if (data.type === 'end') {
        // отклонили на другом моём устройстве — просто закрываем, без укора
        const byMe = data.byUserId === me;
        finish(data.reason === 'declined' && !byMe ? 'Звонок отклонён' : '');
        return;
      }

      if (data.type === 'kicked' && data.userId) {
        // Меня выставил администратор: комнату медиасервера он уже погасил,
        // остаётся сказать за что. Чужое удаление — просто минус участник.
        if (data.userId === me) finish('Вас удалили из звонка');
        else setRoster((list) => list.filter((p) => p.id !== data.userId));
        return;
      }

      if (data.type === 'closed') {
        finish();
        return;
      }

      /*
       * Роль ведущего сменилась. Событие приходило с сервера и раньше, но
       * никто его не слушал: у прежнего ведущего кнопки оставались, у нового
       * не появлялись до перезахода (замечание Ильи 15.09.2026). Роль одна,
       * поэтому все четыре поля указывают на одного человека.
       */
      if (data.type === 'host' && data.userId) {
        const host = data.userId;
        const mine = host === me;
        setCore((s) => ({
          ...s,
          adminId: host,
          presenterId: host,
          boardHostId: host,
          screenShareHolderId: host,
        }));
        // Показ экрана — право ведущего: роль ушла, показ прекращается сам.
        if (shouldStopScreenShare({ newPresenterId: host, me, screenOn: coreRef.current.screenOn })) {
          stopScreen();
        }
        const who = rosterRef.current.find((p) => p.id === host)?.name ?? 'собеседника';
        notify(mine ? 'Вы теперь ведущий' : `Ведущий — ${who}`);
        return;
      }

      if (data.type === 'presenter' && data.userId) {
        const mine = data.userId === me;
        setCore((s) => ({ ...s, presenterId: data.userId!, screenShareHolderId: data.userId! }));
        // право ушло, пока я показывал — показ останавливается сам; ведущий
        // показывать вправе всегда, у него показ не гасится
        if (
          coreRef.current.boardHostId !== me &&
          shouldStopScreenShare({ newPresenterId: data.userId, me, screenOn: coreRef.current.screenOn })
        ) {
          stopScreen();
        }
        const who = rosterRef.current.find((p) => p.id === data.userId)?.name ?? 'собеседника';
        notify(mine ? 'Право показа у вас' : `Право показа у ${who}`);
        return;
      }

      if (data.type === 'signal' && data.from && data.payload?.state) {
        const from = data.from;
        const hand = !!data.payload.state.hand;
        if (!handsRef.current[from] && hand) playSound('hand');
        handsRef.current = { ...handsRef.current, [from]: hand };
        setHands(handsRef.current);
      }

      // новое сообщение переписки: лента дописывается, счётчик растёт —
      // обнуляет его экран, когда панель открыта (markChatRead)
      if (data.type === 'callMessage') {
        const msg = (data as { message?: CallMessage }).message;
        if (msg) {
          setMessages((list) => (list.some((m) => m.id === msg.id) ? list : [...list, msg]));
          if (msg.author.id !== meRef.current) setUnread((n) => n + 1);
        }
        return;
      }

      // отсчёт завели, сменили или сняли: строка звонка уже поменялась
      if (data.type === 'timer') {
        setCore((s) => ({ ...s, timerEndsAt: (data as { endsAt?: string | null }).endsAt ?? null }));
      }

      // доска: событие не несёт лист целиком (раздел 4.2 договора) — только знак перечитать его
      if (data.type === 'sheetOpened' || data.type === 'sheetClosed') {
        setCore((s) => ({ ...s, boardSignal: s.boardSignal + 1 }));
      }
    },
    [applyView, finish, me, notify, setStatus, stopScreen],
  );

  // сотрудник слышит звонок общей шиной чата
  useEffect(() => {
    if (isGuest) return;
    return chat.subscribe(onCallEvent);
  }, [chat, isGuest, onCallEvent]);

  /*
   * Гость: свой поток событий той же комнаты. Шины чата у него нет — нет и
   * учётной записи, которой она адресует события.
   */
  useEffect(() => {
    const token = guestRef.current?.token;
    if (!token) return;
    const es = new EventSource(`/api/rooms/${encodeURIComponent(token)}/stream`, {
      withCredentials: true,
    });
    es.onmessage = (ev) => {
      let event: ChatEvent;
      try {
        event = JSON.parse(ev.data);
      } catch {
        return;
      }
      onCallEvent(event);
    };
    es.onerror = () => {
      // CLOSED — сервер отказал (гостя отцепили, звонка больше нет) и сам
      // источник больше не переподключится; временный обрыв сети браузер
      // чинит переподключением молча, здесь его не трогаем
      if (es.readyState !== EventSource.CLOSED) return;
      if (coreRef.current.phase === 'ended') return;
      finish('Связь потеряна');
    };
    return () => es.close();
  }, [isGuest, onCallEvent, finish]);

  /*
   * Разговор — это присутствие (баг Ильи 17.09.2026): автовыход по
   * бездействию считал активностью только мышь и клавиатуру, и посреди
   * звонка обоих собеседников выбрасывало из системы, а разговор обрывался.
   * Пока звонок идёт, вкладка сама говорит слежению, что человек здесь.
   *
   * Гостя это не касается: у него нет учётной записи, из которой выходить.
   */
  useEffect(() => {
    if (isGuest) return;
    if (core.phase === 'idle' || core.phase === 'ended') return;
    markActivity();
    const t = setInterval(markActivity, ACTIVITY_PING_MS);
    return () => clearInterval(t);
  }, [core.phase, isGuest]);

  /*
   * Дозвон не длится вечно (жалоба Ильи 21.09.2026: «звонил кто-то, звонка не
   * видно, прервать нечем — помогла только перезагрузка»). Экран входящего
   * держится, пока не придёт весть о конце звонка; если её потеряли, он
   * звонил бы бесконечно. Через срок дозвона спрашиваем сервер сами: звонка
   * нет или он уже не мой — закрываем.
   */
  useEffect(() => {
    if (isGuest || core.phase !== 'incoming') return;
    const t = setTimeout(() => {
      const id = callId.current;
      if (!id) return;
      api
        .callCurrent()
        .then((cur) => {
          if (cur && cur.id === id && cur.participants.find((p) => p.id === me)?.status === 'ringing') return;
          finish('');
        })
        .catch(() => {});
    }, RING_WATCH_MS);
    return () => clearTimeout(t);
  }, [core.phase, finish, isGuest, me]);

  // Звук звонка: входящему — мелодия, позвонившему — гудки дозванивания.
  // Перестал звенеть — системное уведомление «Входящий звонок» убираем: оно
  // могло прийти пушем, пока вкладка спала.
  const ringingCall = useRef<string | null>(null);
  useEffect(() => {
    if (core.phase === 'incoming') {
      ringingCall.current = callId.current;
      startLoop('incoming');
    } else {
      const was = ringingCall.current;
      ringingCall.current = null;
      if (was) void closeShown((tag) => tag === `call-${was}`);
      if (core.phase === 'outgoing') startLoop('outgoing');
      else stopLoops();
    }
    return stopLoops;
  }, [core.phase]);

  // Телефон: полный экран звонка — запись в истории; «назад» сворачивает.
  useEffect(() => {
    if (!mobile) return;
    const shown = core.phase !== 'idle' && core.phase !== 'ended' && core.view === 'full';
    if (shown) pushCallEntry();
    else if (hasCallEntry()) popCallEntry();
  }, [mobile, core.phase, core.view]);

  useEffect(() => {
    if (!mobile) return;
    // у гостя сворачивать некуда — за его звонком нет приложения, свернутое
    // окошко оставило бы его перед пустым экраном с полоской
    if (isGuest) return;
    return watchCallPop(() => {
      // запись ушла по «назад»: сворачиваем, разговор продолжается
      setCore((s) => (s.phase === 'idle' || s.phase === 'ended' ? s : { ...s, view: 'mini' }));
    });
  }, [mobile, isGuest]);

  // счётчик длительности
  useEffect(() => {
    if (core.phase !== 'active') return;
    const timer = setInterval(() => setCore((s) => ({ ...s, seconds: s.seconds + 1 })), 1000);
    return () => clearInterval(timer);
  }, [core.phase]);

  // Вкладку открыли посреди разговора — подхватываем незавершённый звонок.
  // То же при возвращении из фона: на телефоне приглашение пришло пушем,
  // пока поток событий был закрыт, и по нажатию на уведомление нужно
  // показать входящий.
  useEffect(() => {
    if (isGuest) return;
    const pickUp = () => {
      if (document.visibilityState === 'hidden') return;
      // Идёт разговор — спрашивать сервер не о чем. А вот показанный входящий
      // проверяем: пока вкладка спала, звонок мог кончиться, и экран остался
      // бы висеть с кнопкой, которая ничего не делает (баг Ильи 16.09.2026).
      if (callId.current && coreRef.current.phase !== 'incoming') return;
      api
        .callCurrent()
        .then((cur) => {
          const stale = callId.current && coreRef.current.phase === 'incoming';
          if (!cur || cur.adminId === me) {
            // Звонка больше нет: закрываем устаревший экран входящего.
            // Телефон открыли, а звонить нечему — висящие «Входящие» о
            // прошлых звонках не нужны (их уборка могла потеряться при
            // перезапуске сервера). «Пропущенный» и «Итоги» остаются.
            void closeShown(isRingingCallTag);
            if (stale) finish('');
            return;
          }
          if (cur.participants.find((p) => p.id === me)?.status !== 'ringing') {
            void closeShown(isRingingCallTag);
            if (stale) finish('');
            return;
          }
          /*
           * Дозвон длится минуту. Строка «звонит», пережившая этот срок, —
           * след оборванного разговора, а не приглашение: показывать по ней
           * входящий нельзя (баг Ильи 21.09.2026). Такой экран запоминал
           * номер мёртвого звонка, и следующие приглашения вкладка молча
           * отклоняла.
           */
          if (ringingTooOld(cur.startedAt)) {
            void closeShown(isRingingCallTag);
            if (stale) finish('');
            return;
          }
          if (callId.current === cur.id) return;
          setCore((s) => ({
            ...EMPTY,
            phase: 'incoming',
            kind: cur.kind,
            view: s.view,
            speakerId: s.speakerId,
            stripShown: s.stripShown,
            camOn: cur.kind === 'video',
          }));
          applyView(cur);
        })
        .catch(() => {});
    };
    pickUp();
    document.addEventListener('visibilitychange', pickUp);
    window.addEventListener(STREAM_OPENED, pickUp);
    return () => {
      document.removeEventListener('visibilitychange', pickUp);
      window.removeEventListener(STREAM_OPENED, pickUp);
    };
  }, [applyView, finish, isGuest, me]);

  /*
   * Вход гостя. Один раз за жизнь провайдера: `roomJoin` страница уже сделала,
   * здесь остаётся принять вид звонка и войти в комнату медиасервера.
   */
  const entered = useRef(false);
  useEffect(() => {
    if (!guest || entered.current) return;
    entered.current = true;
    const kind = guest.call.kind;
    applyView(guest.call);
    setCore((s) => ({ ...s, phase: 'active', kind, camOn: kind === 'video' }));
    joining.current = true;
    connectRoom(guest.url, guest.mediaToken, kind)
      .catch((e) => finish(e instanceof Error ? e.message : 'Не удалось войти в звонок'))
      .finally(() => {
        joining.current = false;
      });
  }, [applyView, connectRoom, finish, guest]);

  useEffect(() => cleanup, [cleanup]);

  const participants = useMemo(
    () => buildParticipants({ roster, media, speakers, hands, me, mine: core }),
    [core, hands, me, media, roster, speakers],
  );

  // Автомикшер (спека 2026-09-25-call-automixer): громкость каждого
  // собеседника у меня в наушниках.
  const others = useMemo(
    () => participants.filter((p) => !p.isMe && p.status === 'active').map((p) => p.id),
    [participants],
  );
  const othersKey = others.join(',');
  const gains = useMemo(
    () => mixerGains({ others, speakers, lastHeard: lastHeard.current, now: Date.now() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [othersKey, speakers, mixTick],
  );
  useEffect(() => {
    const next = mixerNextChange({ speakers, lastHeard: lastHeard.current, now: Date.now() });
    if (next === null) return;
    const t = setTimeout(() => setMixTick((n) => n + 1), Math.max(next - Date.now(), 0) + 20);
    return () => clearTimeout(t);
  }, [speakers, mixTick]);

  const value = useMemo<CallCtx>(() => {
    // «одна демонстрация на звонок»: чужой показ закрывает мою кнопку
    const otherScreen = participants.some((p) => !p.isMe && p.screenOn);
    return {
      ...core,
      participants,
      gains,
      me,
      // у гостя прав администратора нет по определению: он не заводил звонок
      isAdmin: !isGuest && core.adminId === me,
      isGuest,
      link: core.linkToken ? `${window.location.origin}/room/${core.linkToken}` : '',
      screenSupported,
      canShare: canShareMath({
        supported: screenSupported,
        active: core.phase === 'active',
        // показывает держатель права, а ведущий — всегда (гость ведущим не бывает)
        iAmPresenter: core.screenShareHolderId === me || (!isGuest && core.boardHostId === me),
        peerScreen: otherScreen,
        screenOn: core.screenOn,
        peerName: '',
      }),
      attach,
      trackKeys,
      resumeAudio,
      sendBoardData,
      onBoardData,
      sendReaction,
      reaction,
      messages,
      unread,
      loadMessages,
      sendMessage,
      uploadMessageFile,
      markChatRead,
      call,
      callChannel,
      callPeople,
      invite,
      kick,
      closeRoom,
      accept,
      joinMeeting,
      decline,
      hangup,
      toggleMic,
      toggleCam,
      toggleHand,
      startScreen,
      stopScreen,
      setHost,
      setScreenShare,
      switchInput,
      switchSpeaker,
      flipCamera,
      minimize,
      expand,
      setLayout,
      toggleStrip,
      clearNeedPick,
    };
  }, [
    accept,
    attach,
    call,
    callChannel,
    callPeople,
    clearNeedPick,
    closeRoom,
    core,
    decline,
    expand,
    flipCamera,
    gains,
    hangup,
    invite,
    isGuest,
    joinMeeting,
    kick,
    me,
    minimize,
    onBoardData,
    participants,
    setHost,
    setScreenShare,
    loadMessages,
    markChatRead,
    messages,
    reaction,
    resumeAudio,
    sendBoardData,
    sendMessage,
    sendReaction,
    unread,
    uploadMessageFile,
    setLayout,
    startScreen,
    stopScreen,
    switchInput,
    switchSpeaker,
    toggleCam,
    toggleHand,
    toggleMic,
    toggleStrip,
    trackKeys,
  ]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
